import type { AcousticRoomSignal } from '../liveLessonTypes';
import { clamp01, mean } from './utils';
import type { AcousticDecisionProfile } from '../liveLessonTypes';
import type {
  AcousticCalibrationRadarAxisKey,
  AcousticCalibrationSnapshot,
  AcousticCalibrationTag,
  AcousticDecisionRadarAxisKey
} from './calibrationTypes';
import { ACOUSTIC_CALIBRATION_TAGS } from './calibrationTypes';

function situationScoresFromSignal(signal: AcousticRoomSignal): AcousticCalibrationSnapshot['situationScores'] {
  if (signal.situationScoreMap) {
    return { ...signal.situationScoreMap };
  }
  const base = { single_speaker: 0, conversation: 0, babble: 0, shouting: 0 };
  for (const entry of signal.situationScores) {
    if (entry.key in base) {
      base[entry.key] = entry.score;
    }
  }
  return base;
}

export function extractDecisionRadarValues(
  signal: AcousticRoomSignal
): Record<AcousticDecisionRadarAxisKey, number> {
  const profile = signal.decisionProfile;
  const integrated = profile?.integratedScores ?? signal.situationScoreMap;
  const frame = profile?.frameFusionScores;
  const yamnet = profile?.yamnet;
  const fusion = profile?.fusion;

  return {
    yamnet_speech: clamp01(yamnet?.speech ?? 0),
    yamnet_conversation: clamp01(yamnet?.conversation ?? 0),
    yamnet_babble: clamp01(yamnet?.babble ?? 0),
    yamnet_shouting: clamp01(yamnet?.shouting ?? 0),
    fusion_loudness: clamp01(fusion?.loudness ?? signal.loudnessNormalized),
    fusion_noise: clamp01(fusion?.noiseIntensity ?? 0),
    fusion_turnTaking: clamp01(fusion?.turnTaking ?? 0),
    fusion_diffuseness: clamp01(fusion?.diffuseness ?? signal.globalFeatures?.diffuseness ?? 0),
    fusion_coherence: clamp01(fusion?.speechCoherence ?? 0),
    fusion_pausePulse: clamp01(fusion?.pausePulse ?? signal.globalFeatures?.pausePulse ?? 0),
    fusion_crest: clamp01((fusion?.crestFactor ?? 0) / 6),
    fusion_peaks: clamp01((fusion?.peakDensity ?? signal.peakDensity) * 10),
    frame_single_speaker: clamp01(frame?.single_speaker ?? 0),
    frame_conversation: clamp01(frame?.conversation ?? 0),
    frame_babble: clamp01(frame?.babble ?? 0),
    frame_shouting: clamp01(frame?.shouting ?? 0),
    integrated_single_speaker: clamp01(integrated?.single_speaker ?? 0),
    integrated_conversation: clamp01(integrated?.conversation ?? 0),
    integrated_babble: clamp01(integrated?.babble ?? 0),
    integrated_shouting: clamp01(integrated?.shouting ?? 0)
  };
}

function averageDecisionProfiles(signals: AcousticRoomSignal[]): Record<AcousticDecisionRadarAxisKey, number> {
  const keys = Object.keys(extractDecisionRadarValues(signals[0])) as AcousticDecisionRadarAxisKey[];
  return Object.fromEntries(
    keys.map((key) => [key, mean(signals.map((s) => extractDecisionRadarValues(s)[key]))])
  ) as Record<AcousticDecisionRadarAxisKey, number>;
}

function mergeDecisionProfile(signals: AcousticRoomSignal[]): AcousticDecisionProfile | undefined {
  const withProfile = signals.filter((s) => s.decisionProfile);
  if (!withProfile.length) return undefined;
  const last = withProfile[withProfile.length - 1].decisionProfile!;
  const avg = averageDecisionProfiles(withProfile);
  return {
    yamnet: {
      speech: avg.yamnet_speech,
      conversation: avg.yamnet_conversation,
      babble: avg.yamnet_babble,
      shouting: avg.yamnet_shouting
    },
    fusion: {
      loudness: avg.fusion_loudness,
      noiseIntensity: avg.fusion_noise,
      turnTaking: avg.fusion_turnTaking,
      diffuseness: avg.fusion_diffuseness,
      speechCoherence: avg.fusion_coherence,
      pausePulse: avg.fusion_pausePulse,
      crestFactor: avg.fusion_crest * 6,
      peakDensity: avg.fusion_peaks / 10,
      effectiveHasSpeech: last.fusion.effectiveHasSpeech
    },
    frameFusionScores: {
      single_speaker: avg.frame_single_speaker,
      conversation: avg.frame_conversation,
      babble: avg.frame_babble,
      shouting: avg.frame_shouting
    },
    integratedScores: {
      single_speaker: avg.integrated_single_speaker,
      conversation: avg.integrated_conversation,
      babble: avg.integrated_babble,
      shouting: avg.integrated_shouting
    },
    classroomModes: last.classroomModes
  };
}

export function extractRadarValues(signal: AcousticRoomSignal): Record<AcousticCalibrationRadarAxisKey, number> {
  const global = signal.globalFeatures;
  return {
    loudness: clamp01(signal.loudnessNormalized),
    dominance: clamp01(global?.dominance ?? 0),
    diffuseness: clamp01(global?.diffuseness ?? 0),
    volatility: clamp01(global?.volatility ?? 0),
    rhythmicity: clamp01(global?.rhythmicity ?? 0),
    entropy: clamp01(global?.entropy ?? 0),
    peakDensity: clamp01(signal.peakDensity * 10),
    spectralStability: clamp01(signal.spectralStability),
    rhythmRegularity: clamp01(signal.rhythmRegularity),
    changeScore: clamp01(signal.changeScore)
  };
}

export function averageSignals(signals: AcousticRoomSignal[]): AcousticRoomSignal | null {
  if (!signals.length) return null;
  if (signals.length === 1) return signals[0];

  const radarKeys = Object.keys(extractRadarValues(signals[0])) as AcousticCalibrationRadarAxisKey[];
  const avgRadar = Object.fromEntries(
    radarKeys.map((key) => [key, mean(signals.map((s) => extractRadarValues(s)[key]))])
  ) as Record<AcousticCalibrationRadarAxisKey, number>;

  const last = signals[signals.length - 1];
  const situationKeys = ['single_speaker', 'conversation', 'babble', 'shouting'] as const;
  const avgSituation = Object.fromEntries(
    situationKeys.map((key) => [
      key,
      mean(signals.map((s) => situationScoresFromSignal(s)[key]))
    ])
  ) as AcousticCalibrationSnapshot['situationScores'];

  const decisionProfile = mergeDecisionProfile(signals);

  return {
    ...last,
    decisionProfile,
    levelDb: mean(signals.map((s) => s.levelDb)),
    loudnessNormalized: avgRadar.loudness,
    peakDensity: avgRadar.peakDensity / 10,
    volatility: avgRadar.volatility,
    volatilityShort: mean(signals.map((s) => s.volatilityShort)),
    volatilityLong: mean(signals.map((s) => s.volatilityLong)),
    changeScore: avgRadar.changeScore,
    rhythmRegularity: avgRadar.rhythmRegularity,
    spectralStability: avgRadar.spectralStability,
    situationConfidence: mean(signals.map((s) => s.situationConfidence)),
    situationScoreMap: avgSituation,
    situationScores: situationKeys.map((key) => ({
      key,
      label: key,
      score: avgSituation[key]
    })),
    globalFeatures: {
      loudness: avgRadar.loudness,
      dominance: avgRadar.dominance,
      diffuseness: avgRadar.diffuseness,
      volatility: avgRadar.volatility,
      rhythmicity: avgRadar.rhythmicity,
      entropy: avgRadar.entropy
    }
  };
}

export function buildCalibrationSnapshot(
  signal: AcousticRoomSignal,
  tag: AcousticCalibrationTag,
  options?: {
    note?: string;
    source?: 'live' | 'file';
    sourceFileName?: string;
  }
): AcousticCalibrationSnapshot {
  const tagMeta = ACOUSTIC_CALIBRATION_TAGS.find((entry) => entry.key === tag);
  return {
    id: crypto.randomUUID(),
    taggedAt: new Date().toISOString(),
    tag,
    tagLabel: tagMeta?.label ?? tag,
    note: options?.note?.trim() || undefined,
    autoDetected: signal.detectedSituation,
    autoConfidence: signal.situationConfidence,
    radar: extractRadarValues(signal),
    decisionRadar: extractDecisionRadarValues(signal),
    source: options?.source,
    sourceFileName: options?.sourceFileName,
    situationScores: situationScoresFromSignal(signal),
    levelDb: signal.levelDb,
    phaseFit: signal.phaseFit
  };
}
